import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BiImport } from "react-icons/bi";
import { CiSearch } from "react-icons/ci";
import { FaFilter } from "react-icons/fa";
import { FaEye } from "react-icons/fa";
import partiesData from '../data/party';
import stocksData from '../data/stock';
import updatestocksApi from '../data/stockhistory';
import settingapi from '../data/setting';
import { PiNutLight } from 'react-icons/pi';


// --- Dynamic AKK Balance Sheet Data Builder (MATCHING IMAGE FORMAT) ---
/**
 * Example output shape:
 * {
 *   liabilities: [
 *     {
 *       group: "Capital Account",
 *       items: [
 *         { label: "AKK CAP OLD SHARES(9.21.22)", amount: "27,25,547.52" },
 *         { label: "AKK CAPITAL(27.07.21)", amount: "12,07,245.25" },
 *         { label: "Interest On Investment", amount: "39,97,449.15" }
 *       ],
 *       subtotal: "1,69,89,163.23"
 *     }, ...
 *   ],
 *   liabTotal: "2,08,56,570.30",
 *   assets: [
 *     ...
 *   ],
 *   assetTotal: "2,08,56,570.30",
 *   settings: { ...meta info like company, period, etc }
 * }
 */
export function buildAkkBalanceSheet({
  capitalOldShares,
  capital,
  interestOnInvestment,
  profitAndLoss,
  sundryCreditors,
  closingStock,
  company = "AKK NEW SHARES",
  location = "Jaipur",
  period = "1-Apr-2023 to 31-Dec-2025",
  asAt = "31-Dec-2025"
}: {
  capitalOldShares: number,
  capital: number,
  interestOnInvestment: number,
  profitAndLoss: number,
  sundryCreditors: number,
  closingStock: number,
  company?: string,
  location?: string,
  period?: string,
  asAt?: string
}) {
  function fmt(num: number) {
    return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  const capitalAccountSubtotal = capitalOldShares + capital + interestOnInvestment;
  const profitAndLossLabel = profitAndLoss < 0
    ? `(${fmt(Math.abs(profitAndLoss))})`
    : fmt(profitAndLoss);

  const liabilities = [
    {
      group: "Capital Account",
      items: [
        { label: "AKK CAP OLD SHARES(9.21.22)", amount: fmt(capitalOldShares) },
        { label: "AKK CAPITAL(27.07.21)", amount: fmt(capital) },
        { label: "Interest On Investment", amount: fmt(interestOnInvestment) },
      ],
      subtotal: fmt(capitalAccountSubtotal)
    },
    {
      group: "Loans (Liability)",
      items: [],
      subtotal: "" // No loans shown in image (empty)
    },
    {
      group: "Profit & Loss A/c",
      items: [
        { label: "Opening Balance", amount: "" },
        { label: "Current Period", amount: profitAndLossLabel }
      ],
      subtotal: fmt(Math.abs(profitAndLoss))
    }
  ];
  // Total is sum of subtotal Capital Account + Profit & Loss A/c (loans empty)
  const liabTotal = fmt(capitalAccountSubtotal + Math.abs(profitAndLoss));

  // --- Assets (RIGHT section from image) ---
  const assets = [
    {
      group: "Current Liabilities",
      items: [
        { label: "Sundry Creditors", amount: fmt(sundryCreditors) }
      ],
      subtotal: fmt(sundryCreditors)
    },
    {
      group: "Investments",
      items: [],
      subtotal: "" // None as per image
    },
    {
      group: "Current Assets",
      items: [
        { label: "Closing Stock", amount: fmt(closingStock) }
      ],
      subtotal: fmt(closingStock)
    }
  ];
  const assetTotal = fmt(sundryCreditors + closingStock);

  return {
    settings: {
      company,
      location,
      period,
      asAt,
    },
    liabilities,
    liabTotal,
    assets,
    assetTotal,
  };
}

type StockCategoryObj = {
  id: number;
  stock_name: string;
  category: string;
  unit: string;
  total_amount: string;
  indirect_charges: string;
  perUnitCharge: string;
  investment_type: string;
};

type WithdrawalRecord = {
  id: number;
  party_id: number;
  amount: number;
  date: string;
  catrgories: string;
};

type SellStockRecord = {
  id: number;
  party_id: number;
  category: string;
  stock_id: string;
  stock_name: string;
  sell_units: string;
  sell_amount: string;
  indirect_charges: string;
  date: string;
  catrgories: string;
};

interface Party {
  id: number;
  date: string;
  name: string;
  number: string;
  contact_person_name: string;
  contact_person_number: string;
  categories: string[];
  total_amount?: number | string;
}

type PartyStockDBType = {
  id: number | string;
  party_id: number | string;
  categories: string | StockCategoryObj[];
  date?: string;
  date_of_purchase?: string;
};

type StockCurrentAmountRecord = {
  id: number;
  stock_id: number;
  cat_id: number;
  current_price: number | string;
  current_price_date: string;
  sundry_creditors?: number;
};

function parseCategoriesForDb(categories: string | StockCategoryObj[]): StockCategoryObj[] {
  if (Array.isArray(categories)) return categories as StockCategoryObj[];
  if (typeof categories === 'string') {
    try {
      const value = categories.trim();
      if (value.startsWith('[')) {
        const arr = JSON.parse(value);
        if (Array.isArray(arr)) return arr;
        return [arr];
      }
    } catch { }
  }
  return [];
}

function getAllCurrentAmountsFor(
  stockId: number | string,
  catId: number | string,
  records: StockCurrentAmountRecord[],
  dateFrom?: string,
  dateTo?: string
) {
  if (!stockId || !catId) return [];
  return records
    .filter(record =>
      String(record.stock_id) === String(stockId) &&
      String(record.cat_id) === String(catId) &&
      (!dateFrom || new Date(record.current_price_date) >= new Date(dateFrom)) &&
      (!dateTo   || new Date(record.current_price_date) <= new Date(dateTo))
    )
    .sort((a, b) => (a.current_price_date < b.current_price_date ? 1 : -1));
}

function getLatestCurrentAmountFor(
  stockId: number | string,
  catId: number | string,
  records: StockCurrentAmountRecord[],
  dateFrom?: string,
  dateTo?: string
) {
  const all = getAllCurrentAmountsFor(stockId, catId, records, dateFrom, dateTo);
  if (all.length === 0) return null;
  return all[0];
}

function downloadCSV(csv: string, filename: string) {
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

function getUniqueValues(array: any[], field: string): string[] {
  const set = new Set<string>();
  array.forEach(item => {
    let value = item[field];
    if (value === undefined || value === null) return;
    set.add(String(value));
  });
  return Array.from(set).filter(v => v !== '');
}

const columnFilters = [
  { key: 'party_name', label: 'Party Name', type: 'string' },
];

type ModalSheetView = "overall" | "final" | "balance" | "QTLY Performance";

function formatPeriod(dateFrom?: string, dateTo?: string) {
  if (!dateFrom && !dateTo) return "All dates";
  if (dateFrom && dateTo) {
    return `${new Date(dateFrom).toLocaleDateString()} to ${new Date(dateTo).toLocaleDateString()}`;
  }
  if (dateFrom) return `From ${new Date(dateFrom).toLocaleDateString()}`;
  if (dateTo) return `Up to ${new Date(dateTo).toLocaleDateString()}`;
  return '';
}
function getMonthDiff(partyDate: string | Date | undefined, to: string | Date | undefined) {
  if (!partyDate) return 1;
  const start = typeof partyDate === "string" ? new Date(partyDate) : partyDate;
  const end = to ? (typeof to === "string" ? new Date(to) : to) : new Date();

  if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
    return 1;
  }
  let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  if (end.getDate() >= start.getDate()) {
    months += 1;
  }
  return months < 1 ? 1 : months;
}

// Add API fetch for sellstocks
async function fetchSellStocks() {
  try {
    const res = await fetch("https://stockaadvikbackend.aadviktech.com/auth/sellstocks");
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

// Fetch Withdrawals
async function fetchWithdrawals(): Promise<WithdrawalRecord[]> {
  try {
    const res = await fetch("https://stockaadvikbackend.aadviktech.com/auth/withdrawals");
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}

// (All types and helpers unchanged up to main component)

function BalanceSheet() {
  const [parties, setParties] = useState<Party[]>([]);
  const [stocks, setStocks] = useState<PartyStockDBType[]>([]);
  const [updates, setUpdates] = useState<StockCurrentAmountRecord[]>([]);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 10;
  const [openFilterDropdown, setOpenFilterDropdown] = useState<string | null>(null);
  const [uniqueColumnValues, setUniqueColumnValues] = useState<{ [key: string]: string[] }>({});
  const [filters, setFilters] = useState<{ [key: string]: string | null }>({});
  const [showFilterPanel, setShowFilterPanel] = useState(false);

  // Date filter state
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  // Multi-modal support
  const [selectedParty, setSelectedParty] = useState<Party | null>(null);
  const [showPartyModal, setShowPartyModal] = useState(false);
  const [partyModalSheet, setPartyModalSheet] = useState<ModalSheetView>("overall");

  // For file import
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Interest percentage from Settings API (default 3%)
  const [interestPercentage, setInterestPercentage] = useState(3);

  // Sell Stocks from API state
  const [sellStocks, setSellStocks] = useState<SellStockRecord[]>([]);

  // Withdrawals state
  const [withdrawals, setWithdrawals] = useState<WithdrawalRecord[]>([]);

  // Fetch interest percentage setting from API on mount
  useEffect(() => {
    async function fetchSettingsApi() {
      try {
        const res = await settingapi();
        if (Array.isArray(res)) {
          const percentageSetting = res.find(
            (item: any) => item.meta_key === "percentage"
          );
          if (percentageSetting && percentageSetting.meta_value !== undefined) {
            const percentValue = parseFloat(percentageSetting.meta_value);
            if (!isNaN(percentValue)) {
              setInterestPercentage(percentValue);
            }
          }
        }
      } catch {}
    }
    fetchSettingsApi();
  }, []);

  // Fetch all parties
  useEffect(() => {
    async function fetchPartiesApi() {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/auth/addparty`, {
          method: "GET",
          headers: { "Content-Type": "application/json" },
        });
        if (!res.ok) return setParties([]);
        const data = await res.json();
        if (Array.isArray(data)) {
          setParties(
            data.map((item: any) => ({
              ...item,
              categories: Array.isArray(item.categories)
                ? item.categories
                : (typeof item.categories === "string"
                  ? (
                    item.categories.trim().startsWith("[")
                      ? JSON.parse(item.categories)
                      : item.categories.split(",").map((c: string) => c.trim())
                  ) : [])
            }))
          );
        } else {
          setParties([]);
        }
      } catch {
        setParties([]);
      }
    }
    fetchPartiesApi();
  }, []);

  const fetchStocks = useCallback(async () => {
    try {
      const res = await stocksData();
      setStocks(Array.isArray(res) ? res : []);
    } catch {
      setStocks([]);
    }
  }, []);

  const fetchUpdates = useCallback(async () => {
    try {
      const data = await updatestocksApi();
      setUpdates(Array.isArray(data) ? data : []);
    } catch {
      setUpdates([]);
    }
  }, []);

  // Fetch sell stocks from API on mount
  useEffect(() => {
    async function loadSellStocks() {
      const sellArr = await fetchSellStocks();
      setSellStocks(sellArr);
    }
    loadSellStocks();
  }, []);

  // Fetch withdrawals from API on mount
  useEffect(() => {
    async function loadWithdrawals() {
      const withdrawalsArr = await fetchWithdrawals();
      setWithdrawals(withdrawalsArr);
    }
    loadWithdrawals();
  }, []);

  useEffect(() => {
    fetchStocks();
    fetchUpdates();
  }, [fetchStocks, fetchUpdates]);

  // Helper: in-range date
  const isInDateRange = (dateStr: string) => {
    if (!dateStr) return true;
    const date = new Date(dateStr);
    const from = dateFrom ? new Date(dateFrom) : null;
    const to = dateTo ? new Date(dateTo) : null;
    if (from && date < from) return false;
    if (to && date > to) return false;
    return true;
  };

  // --- Party Aggregated Rows ---
  // Now includes dividend from stock history in update_history
  const partyAggregatedRows = React.useMemo(() => {
    if (!parties.length || !stocks.length) return [];
    let result: {
      party: Party,
      stocks: Array<{
        stock_name: string,
        records: Array<{
          category: string,
          unit: string,
          date_of_purchase: string,
          opening_balance: number,
          update_history: Array<{
            date: string,
            current_amount: number,
            used: number,
            earning: number,
            profitAndLoss: number,
            dividend?: number
          }>,
          total_purchased: number,
          total_used: number,
          total_earning: number,
          total_profit_and_loss: number,
          indirect_charges: number,
          per_unit_charge: number
        }>
      }>,
      total_purchased: number,
      total_used: number,
      total_earning: number,
      total_indirect_charges: number,
      total_interest: number;
      total_sales_amount?: number;
      total_sales_units?: number;
      total_withdrawal_amount?: number;
      withdrawals_details?: WithdrawalRecord[];
    }[] = [];
    
    for (const party of parties) {
      const partyStocks = stocks.filter(stk => String(stk.party_id) === String(party.id));
      let total_purchased = 0;
      let total_used = 0;
      let total_earning = 0;
      let total_indirect_charges = 0;
      let stock_aggregate: typeof result[0]['stocks'] = [];
      let months = 1;
      const end = dateTo ? new Date(dateTo) : new Date();
      months = getMonthDiff(party.date, end);
      let total_interest = Number(party.total_amount || 0) * (Number(interestPercentage) / 100) * Number(months);

      // --- Sell Data ---
      let total_sales_amount = 0;
      let total_sales_units = 0;

      // Gather sale stock amounts for party using sellStocks in given date range.
      if (sellStocks && Array.isArray(sellStocks)) {
        const filteredSellStocks = sellStocks.filter(
          s =>
            String(s.party_id) === String(party.id) &&
            (!dateFrom || new Date(s.date) >= new Date(dateFrom)) &&
            (!dateTo   || new Date(s.date) <= new Date(dateTo))
        );
        total_sales_amount = filteredSellStocks.reduce(
          (sum, s) => sum + (parseFloat(s.sell_amount) || 0), 0
        );
        total_sales_units = filteredSellStocks.reduce(
          (sum, s) => sum + (parseFloat(s.sell_units) || 0), 0
        );
      }

      // --- Withdrawal Data ---
      let total_withdrawal_amount = 0;
      let withdrawals_details: WithdrawalRecord[] = [];
      if (withdrawals && Array.isArray(withdrawals)) {
        const filteredWithdrawals = withdrawals.filter(
          w =>
            String(w.party_id) === String(party.id) &&
            (!dateFrom || new Date(w.date) >= new Date(dateFrom)) &&
            (!dateTo   || new Date(w.date) <= new Date(dateTo))
        );
        total_withdrawal_amount = filteredWithdrawals.reduce(
          (sum, w) => sum + (parseFloat(String(w.amount)) || 0), 0
        );
        withdrawals_details = filteredWithdrawals;
      }

      for (const stk of partyStocks) {
        const cats = parseCategoriesForDb(stk.categories);
        for (const catObj of cats) {
          const catId = catObj.id ?? '';
          const stock_name = catObj.stock_name;
          const perUnitCharge = Number(catObj.perUnitCharge || 0) || 0;
          const indirectCharges = Number(catObj.indirect_charges || 0) || 0;

          const allHistory = getAllCurrentAmountsFor(stk.id, catId, updates, dateFrom, dateTo);

          const chronHistory = [...allHistory].sort((a, b) => (a.current_price_date > b.current_price_date ? 1 : -1));
          let opening = Number(catObj.total_amount || 0);
          const purchaseDate = stk.date_of_purchase || stk.date || "";
          let inDate = isInDateRange(purchaseDate);
          
          let prevAmount = opening;
          let records = [];
          let totalStockUsed = 0;
          let totalStockEarning = 0;
          let total_profit_and_loss = 0;
          
          if (inDate) {
            records.push({
              category: catObj.category,
              unit: catObj.unit,
              date_of_purchase: purchaseDate,
              opening_balance: opening,
              update_history: [],
              total_purchased: opening,
              total_used: 0,
              total_earning: 0,
              indirect_charges: indirectCharges,
              per_unit_charge: perUnitCharge,
              total_profit_and_loss: 0
            });
          }

          let update_history: Array<{
            date: string,
            current_amount: number,
            used: number,
            earning: number,
            profitAndLoss: number,
          }> = [];

          for (let i = 0; i < chronHistory.length; ++i) {
            const rec = chronHistory[i];
            const thisAmount = Number(rec.current_price || 0);
            const used = thisAmount - prevAmount;
            const earning = thisAmount - (prevAmount + indirectCharges);
            const profitAndLoss = thisAmount - (prevAmount + indirectCharges);
            total_profit_and_loss += profitAndLoss;
            update_history.push({
              date: rec.current_price_date,
              current_amount: thisAmount,
              used,
              earning,
              profitAndLoss
            });
            totalStockUsed += used;
            totalStockEarning += earning;
            prevAmount = thisAmount;
          }

          if (inDate && records.length > 0) {
            (records[0] as any).update_history = update_history;
            (records[0] as any).total_used = totalStockUsed;
            (records[0] as any).total_earning = totalStockEarning;
            (records[0] as any).total_profit_and_loss = total_profit_and_loss;
          }

          if (inDate) {
            total_purchased += opening;
            total_purchased += indirectCharges;
            total_indirect_charges += indirectCharges;
          }
          total_used += totalStockUsed;
          total_earning += totalStockEarning;

          let stockEntry = stock_aggregate.find(s => s.stock_name === stock_name);
          if (!stockEntry) {
            stock_aggregate.push({
              stock_name,
              records: records
            });
          } else {
            stockEntry.records.push(...records);
          }
        }
      }
      total_earning = total_earning > 0 ? (total_earning - total_purchased) : 0;
      if (stock_aggregate.length > 0) {
        result.push({
          party,
          stocks: stock_aggregate,
          total_purchased,
          total_used,
          total_earning,
          total_indirect_charges,
          total_interest,
          total_sales_amount,
          total_sales_units,
          total_withdrawal_amount,
          withdrawals_details,
        });
      }
    }
    return result;
  }, [parties, stocks, updates, dateFrom, dateTo, interestPercentage, sellStocks, withdrawals]);

  // --- Collect Sundry Creditors per party from updates ---
  const partySundryCreditorsMap = React.useMemo(() => {
    if (!updates.length || !stocks.length) return {};
    const stockIdToParty = new Map();
    for (const stk of stocks) {
      stockIdToParty.set(String(stk.id), stk.party_id);
    }
    const partyTotals: Record<string, number> = {};
    for (const update of updates) {
      const party_id = stockIdToParty.get(String(update.stock_id));
      if (!party_id) continue;
      const inDate = (!update.current_price_date ||
        (
          (!dateFrom || new Date(update.current_price_date) >= new Date(dateFrom)) &&
          (!dateTo || new Date(update.current_price_date) <= new Date(dateTo))
        )
      );
      if (!inDate) continue;
      if (typeof update.sundry_creditors !== "undefined" && !isNaN(Number(update.sundry_creditors))) {
        if (!partyTotals[party_id]) partyTotals[party_id] = 0;
        partyTotals[party_id] += Number(update.sundry_creditors);
      }
    }
    return partyTotals;
  }, [updates, stocks, dateFrom, dateTo]);

  function buildFinalSheetDataForParty(party: Party) {
    const partyStockRow = partyAggregatedRows.find(r => r.party.id === party.id);
    let remainingAmount = 0;
    let interestExpenses = 0;
    let openingStock = 0;
    let purchases = 0;
    let directExpenses = 0;
    let sharesPurchased = 0;


    openingStock = parties
      .filter(prt => prt.id === party.id)
      .reduce((sum, prt) => sum + (Number(prt.total_amount) || 0), 0);

    function getMonthDiff(partyDate: string | Date | undefined, to: string | Date | undefined) {
      if (!partyDate) return 1;
      const start = typeof partyDate === "string" ? new Date(partyDate) : partyDate;
      const end = to ? (typeof to === "string" ? new Date(to) : to) : new Date();

      if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
        return 1;
      }
      let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
      if (end.getDate() >= start.getDate()) {
        months += 1;
      }
      return months < 1 ? 1 : months;
    }

    let months = 1;
    const end = dateTo ? new Date(dateTo) : new Date();
    months = getMonthDiff(party.date, end);

    interestExpenses =  Number(party.total_amount || 0) * (Number(interestPercentage) / 100) * Number(months);
    const partyStocksRaw = stocks.filter(stk => String(stk.party_id) === String(party.id));
    for (const stk of partyStocksRaw) {
      const purchaseDate = stk.date_of_purchase || stk.date || "";
      if (isInDateRange(purchaseDate)) {
        const cats = parseCategoriesForDb(stk.categories);
        for (const catObj of cats) {
          purchases += Number(catObj.total_amount || 0);
          if(catObj.investment_type === "structure") {
            sharesPurchased += Number(catObj.total_amount || 0);
          }
          if(catObj.investment_type === "direct") {
            directExpenses += Number(catObj.total_amount || 0);
          }

        }
      }
    }

    // Compute sales from sellStocks API for this party and date range
    let sales = 0;
    if (sellStocks.length > 0) {
      let filteredSales = sellStocks.filter(s =>
        String(s.party_id) === String(party.id) &&
        (!dateFrom || new Date(s.date) >= new Date(dateFrom)) &&
        (!dateTo   || new Date(s.date) <= new Date(dateTo))
      );
      sales = filteredSales.reduce((sum, s) => sum + (parseFloat(s.sell_amount) || 0), 0);
    }

    let closingStock = 0;
    for (const stk of partyStocksRaw) {
      const cats = parseCategoriesForDb(stk.categories);
      for (const catObj of cats) {
        const catId = catObj.id;
        const latest = getLatestCurrentAmountFor(stk.id, catId, updates, dateFrom, dateTo);
        const val = latest ? Number(latest.current_price) : (isInDateRange(stk.date_of_purchase || stk.date || "") ? Number(catObj.total_amount) : 0);
        closingStock += val;
      }
    }

    let indirectExpenses = 0;
    for (const stk of partyStocksRaw) {
      const purchaseDate = stk.date_of_purchase || stk.date || "";
      if (!isInDateRange(purchaseDate)) continue;
      const cats = parseCategoriesForDb(stk.categories);
      for (const catObj of cats) {
        indirectExpenses += Number(catObj.indirect_charges || 0);
      }
    }
 purchases-=sales;
    // Use sales in grossProfit
    let grossProfit = (closingStock) - (purchases+indirectExpenses);
    let indirectIncome = 0;
   
    const partyIdStr = String(party.id);
    if (partySundryCreditorsMap[partyIdStr] !== undefined) {
      indirectIncome = partySundryCreditorsMap[partyIdStr];
    }
    grossProfit+=indirectIncome;
    const netLoss = grossProfit < 0 ? (interestExpenses - (grossProfit )) : (interestExpenses - (grossProfit ));
    const format = (n: number) => n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    remainingAmount = (Number(party.total_amount)-(purchases+indirectExpenses))
    closingStock +=(remainingAmount-interestExpenses-indirectExpenses);

    return {
      openingStock: format(openingStock),
      remainingAmount:format(remainingAmount),
      purchases: format(purchases),
      sales: format(sales),
      closingStock: format(closingStock),
      grossProfit: format(grossProfit),
      indirectExpenses: format(indirectExpenses),
      interestExpenses: format(interestExpenses),
      indirectIncome: format(indirectIncome),
      netLoss: format(netLoss),
      leftTotalTrading: format(purchases + Math.max(grossProfit, 0)),
      rightTotalTrading: format(sales + closingStock),
      leftTotalPL: format((openingStock)-netLoss),
      rightTotalPL: format((openingStock)-netLoss),
      directExpenses: format(directExpenses),
      sharesPurchased: format(sharesPurchased),
      raw: {
        openingStock, purchases, sales, closingStock, grossProfit, indirectExpenses, indirectIncome, netLoss
      }
    };
  }

  function buildBalanceSheetDetailsForParty(party: Party) {
    const pnl = buildFinalSheetDataForParty(party);

    const openingStock = Number(pnl.raw.openingStock || 0);
    const investedCapital = Number(pnl.raw.purchases || 0);

    let totalInterest = 0;
    const partyStocksRaw = stocks.filter(stk => String(stk.party_id) === String(party.id));

    // Get the party's investment date (using the earliest stock date for safety)
    let earliestDate = undefined;
    for (const stk of partyStocksRaw) {
      const purchaseDate = stk.date_of_purchase || stk.date || "";
      if (!purchaseDate) continue;
      if (!earliestDate || new Date(purchaseDate) < new Date(earliestDate)) {
        earliestDate = purchaseDate;
      }
    }
    // Use filter or party?.date as a fallback
    let investmentDate = earliestDate || party?.date;
    let endDate = dateTo || new Date();

    function getMonthDiff(partyDate: string | Date | undefined, to: string | Date | undefined) {
      if (!partyDate) return 1;
      const start = typeof partyDate === "string" ? new Date(partyDate) : partyDate;
      const end = to ? (typeof to === "string" ? new Date(to) : to) : new Date();

      if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
        return 1;
      }

      let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());

      if (end.getDate() >= start.getDate()) {
        months += 1;
      }

      return months < 1 ? 1 : months;
    }

    let months = 1;
    if (investmentDate) {
      const start = new Date(investmentDate);
      const end = dateTo ? new Date(dateTo) : new Date();
      months = getMonthDiff(party.date, end);
    }

    let openingBalance = 0;
    for (const stk of partyStocksRaw) {
      const purchaseDate = stk.date_of_purchase || stk.date || "";
      if (!isInDateRange(purchaseDate)) continue;
      const cats = parseCategoriesForDb(stk.categories);
      for (const catObj of cats) {
        openingBalance += Number(catObj.total_amount || 0);
      }
    }

    totalInterest = Number(party.total_amount || 0) * (Number(interestPercentage) / 100) * Number(months);

    const netLoss = Number(pnl.raw.netLoss) > 0 ? -1 * Number(pnl.raw.netLoss) : 0;

    let sundryCreditors = 0;
    let indirectcharges = 0;
    const partyIdStr = String(party.id);
    if (partySundryCreditorsMap[partyIdStr] !== undefined) {
      sundryCreditors = partySundryCreditorsMap[partyIdStr];
    } else {
      for (const stk of partyStocksRaw) {
        const purchaseDate = stk.date_of_purchase || stk.date || "";
        if (!isInDateRange(purchaseDate)) continue;
        const cats = parseCategoriesForDb(stk.categories);
        for (const catObj of cats) { }
      }
    }
    for (const stk of partyStocksRaw) {
      const purchaseDate = stk.date_of_purchase || stk.date || "";
      if (!isInDateRange(purchaseDate)) continue;
      const cats = parseCategoriesForDb(stk.categories);
      for (const catObj of cats) {
        indirectcharges += Number(catObj.indirect_charges || 0);
      }
    }

    const sharesInvestments = investedCapital;
    const closingStock = Number(pnl.raw.closingStock || 0);
    const totalCapital = investedCapital + totalInterest;
    const totalLiabilities = totalCapital + netLoss + sundryCreditors+indirectcharges+(closingStock-sharesInvestments);
    const TotalCloseStockAssests = totalCapital+(closingStock-sharesInvestments);
    const totalAssets = parseFloat(Number(Number(pnl.raw.closingStock) + sundryCreditors).toFixed(2));

    const fmt = (v: number) =>
      v === 0 ? "0.00"
        : v < 0
        ? `(${Math.abs(v).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
        : v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    return {
      liabilities: [
        { label: <b>Capital Account</b>, amount: "" },
        { label: "Total Amount", amount: openingStock },
        { 
          label: "Purchases", 
          amount: isNaN(Number(pnl.raw.purchases)) && isNaN(Number(indirectcharges)) 
            ? "0.00" 
            : Number(Number(pnl.raw.purchases || 0) + Number(indirectcharges || 0)) 
        },
        // { label: "Remaining Amount", amount: String(pnl.remainingAmount || 0) },
        { label: `Interest on Investment (${interestPercentage}%)`, amount: totalInterest },
  
        null,
        { label: <b>Profit &amp; Loss A/c</b>, amount: (pnl.raw.grossProfit) },
        // { label: "Net Loss (Current Period)", amount: (pnl.raw.grossProfit)<0?(pnl.raw.grossProfit):0 },
        // { label: "Net Profit (Current Period)", amount: (pnl.raw.grossProfit)>0?(pnl.raw.grossProfit):0 },
        null,
        { label: <b>Current Liabilities</b>, amount: "" },
        { label: "Indirect Charges", amount: indirectcharges },
        null,
        { label: <b>TOTAL</b>, amount: (pnl.leftTotalPL) },
      ],
      assets: [
        { label: <b>Current Assests</b>, amount: "" },
        { label: "Sundry Debtors", amount: String(pnl.remainingAmount || 0) },
        null,
        { label: <b>Current Assets</b>, amount: "" },
        { label: "Closing Stock", amount: pnl.leftTotalPL },
        null,
        { label: <b>TOTAL</b>, amount:  pnl.leftTotalPL}
      ]
    }
  }

  useEffect(() => {
    if (!partyAggregatedRows.length) return;
    let vals: { [key: string]: string[] } = {};
    vals['party_name'] = partyAggregatedRows.map(row => row.party.name);
    setUniqueColumnValues(vals);
  }, [partyAggregatedRows]);

  const filteredData = React.useMemo(() => {
    let arr = partyAggregatedRows;
    if (search) {
      arr = arr.filter(row =>
        row.party.name && row.party.name.toLowerCase().includes(search.toLowerCase())
      );
    }
    Object.entries(filters).forEach(([key, value]) => {
      if (!value) return;
      if (key === 'party_name') {
        arr = arr.filter(row =>
          row.party.name && row.party.name.toLowerCase() === String(value).toLowerCase()
        );
      }
    });
    return arr;
  }, [partyAggregatedRows, search, filters]);

  const totalPage = Math.ceil(filteredData.length / perPage) || 1;
  const pageData = filteredData.slice((page - 1) * perPage, page * perPage);

  function handleExportCSV() {
    const exportHeadings = [
      "Party Name", "Contact Person", "Contact Number", "Total Purchased", "Total Used", "Total Earning"
    ];
    let csv = [exportHeadings.join(",")];
    pageData.forEach(row => {
      csv.push([
        row.party.name,
        row.party.contact_person_name,
        row.party.contact_person_number,
        row.total_purchased,
        row.total_used,
        row.total_earning
      ].map(val => `"${val !== undefined && val !== null ? String(val).replace(/"/g, '""') : ''}"`).join(","))
    });
    downloadCSV(csv.join("\r\n"), "party_balance_sheet.csv");
  }

  function handleImportClick() {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
      fileInputRef.current.click();
    }
  }
  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (event) => {
      const text = event.target?.result as string;
      alert("Import functionality: Received file, implement parsing here.");
    };
    reader.readAsText(file);
  }

  const handleColumnFilterChange = (colKey: string, val: string | null) => {
    setFilters(filters => ({
      ...filters,
      [colKey]: val
    }));
    setPage(1);
    setOpenFilterDropdown(null);
  };

  const renderGlobalFilterPanel = () => (
    showFilterPanel && (
      <div className="fixed top-0 left-0 right-0 bottom-0 bg-black/30 z-30 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-6 w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto relative">
          <button
            className="absolute top-3 right-4 text-xl text-gray-500 hover:text-gray-700"
            onClick={() => setShowFilterPanel(false)}
            title="Close"
          >&times;</button>
          <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <FaFilter className="text-[#6742c7]" /> Filters
          </h3>
          <div className="space-y-3">
            {columnFilters.map(f => (
              <div key={f.key}>
                <div className="text-xs font-semibold text-gray-600 mb-1">{f.label}</div>
                {uniqueColumnValues[f.key] && uniqueColumnValues[f.key].length > 0 ? (
                  <div className="flex gap-2 flex-wrap">
                    <button
                      key="filter-all"
                      className={`px-3 py-1 rounded border text-xs ${!filters[f.key] ? "bg-[#6742c7] text-white" : "bg-gray-100 text-gray-700"}`}
                      onClick={() => handleColumnFilterChange(f.key, null)}
                    >
                      All
                    </button>
                    {uniqueColumnValues[f.key]
                      .sort()
                      .map(option => (
                        <button
                          key={option + f.key}
                          className={`px-3 py-1 rounded border text-xs ${filters[f.key] === option
                            ? "bg-[#6742c7] text-white"
                            : "bg-gray-100 text-gray-700"
                          }`}
                          onClick={() => handleColumnFilterChange(f.key, option)}
                        >
                          {option}
                        </button>
                      ))
                    }
                  </div>
                ) : (
                  <span className="block px-2 text-xs text-gray-400">No values</span>
                )}
                {filters[f.key] && (
                  <button
                    className="mt-1 px-2 py-0 text-xs text-gray-500 hover:text-[#6742c7]"
                    onClick={() => handleColumnFilterChange(f.key, null)}
                  >
                    Clear
                  </button>
                )}
              </div>
            ))}
            {/* Date Range Picker */}
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Date Range</label>
              <div className="flex gap-2 items-center">
                <input
                  type="date"
                  className="border px-2 py-1 rounded text-xs"
                  value={dateFrom}
                  onChange={e => { setDateFrom(e.target.value); setPage(1); }}
                  max={dateTo || undefined}
                  style={{width:"130px"}}
                />
                <span className="text-gray-500">to</span>
                <input
                  type="date"
                  className="border px-2 py-1 rounded text-xs"
                  value={dateTo}
                  onChange={e => { setDateTo(e.target.value); setPage(1); }}
                  min={dateFrom || undefined}
                  style={{width:"130px"}}
                />
              </div>
              {(dateFrom || dateTo) && (
                <button
                  className="mt-1 px-2 py-0 text-xs text-gray-500 hover:text-[#6742c7]"
                  onClick={() => { setDateFrom(''); setDateTo(''); setPage(1); }}
                >
                  Clear Date
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  );

  function renderDatePeriodBanner() {
    const period = formatPeriod(dateFrom, dateTo);
    return (
      <div className="mb-2 text-sm text-[#6742c7] font-medium flex items-center gap-2">
        <span>Showing data for:</span>
        <span className="bg-[#ffff] px-2 rounded text-[#222]">
          {period}
        </span>
      </div>
    );
  }

  function renderPnLModalContent(party: Party) {
    const data = buildFinalSheetDataForParty(party);
    console.log(" data ", data);
    const periodLabel = (dateFrom || dateTo)
      ? `${dateFrom ? new Date(dateFrom).toLocaleDateString("en-GB") : ""}${
          dateFrom && dateTo ? " to " : dateTo ? "to " : ""
        }${dateTo ? new Date(dateTo).toLocaleDateString("en-GB") : !dateTo ? " to 31-Dec-2025" : ""}`
      : "Select date range";

    // Helper for formatting currency with commas and two decimals
    const fmt = (n: any) =>
      typeof n === "number"
        ? n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : n || "";

    // Prepare Indirect Expenses total (add Indirect + Interest)
    const indirectExpensesVal = Number((data.indirectExpenses ?? "0").toString().replace(/,/g, ""));
    const interestExpensesVal = Number((data.interestExpenses ?? "0").toString().replace(/,/g, ""));
    const totalIndirectExpenses = indirectExpensesVal + interestExpensesVal;

    // Table cell helper
    const renderCell = (
      children: React.ReactNode,
      { bold, align = "left", fontSize = 15, style = {}, colSpan, italic }: any = {}
    ) => (
      <td
        style={{
          border: "0px solid #333",
          padding: "7px 8px",
          fontWeight: bold ? 700 : undefined,
          fontStyle: italic ? "italic" : undefined,
          textAlign: align,
          fontSize,
          ...style,
          background: style && style.background ? style.background : undefined,
        }}
        colSpan={colSpan}
      >
        {children}
      </td>
    );

    function addvalue(arg0: string, indirectExpenses: string): number {
      const a = Number(arg0?.toString().replace(/,/g, "") || "0");
      const b = Number(indirectExpenses?.toString().replace(/,/g, "") || "0");
      return a + b;
    }

    return (
      <div style={{ background: "#fff", maxWidth: 900, margin: "0 auto", padding: 12 }}>
        <h2 style={{ fontWeight: 700, color: "#000", textAlign: "center", fontSize: 19, marginBottom: 0, letterSpacing: 1 }}>
          AKK NEW SHARES
        </h2>
        <div style={{ textAlign: "center", fontSize: 15, marginBottom: 6 }}>Jaipur</div>
        <div style={{ textAlign: "center", fontWeight: 700, fontSize: 18, margin: "0 0 2px 0" }}>
          Profit &amp; Loss A/c
        </div>
        <div style={{ textAlign: "center", fontSize: 13, marginBottom: 6 }}>
          {periodLabel}
        </div>

        <div style={{ overflowX: "auto", marginTop: 11 }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 700 }}>
            <thead>
              <tr style={{ border: "1px solid #0000006e", borderLeft: "none", borderRight: "none" }}>
                {renderCell("Particulars", { bold: true, align: "center", style: { background: "#ffff" } })}
                {renderCell(periodLabel, { bold: true, align: "center", style: { background: "#ffff" } })}
                {renderCell("Particulars", { bold: true, align: "center", style: { background: "#ffff" } })}
                {renderCell(periodLabel, { bold: true, align: "center", style: { background: "#ffff" } })}
              </tr>
            </thead>
            <tbody>
              <tr>
                {renderCell("Total Amount")}
                {renderCell(fmt(data.openingStock), { align: "right" })}
                {renderCell("Sales Accounts")}
                {renderCell(fmt(data.sales), { align: "right" })}
              </tr>
              <tr className='hidden'>
                {renderCell("Remaining Amount")}
                {renderCell(fmt(data.remainingAmount), { align: "right" })}
                {renderCell("")}
                {renderCell("")}
              </tr>
              <tr>
                {renderCell("Purchase Accounts")}
                {renderCell(fmt(addvalue(data.purchases , data.indirectExpenses)), { align: "right" })}
                {renderCell("Closing Stock")}
                {renderCell(fmt(data.rightTotalPL), { align: "right" })}
              </tr>
              <tr className=''>
                {renderCell(<span style={{ marginLeft: 12 }}>Shares Purchased</span>)}
                {renderCell(fmt(data.sharesPurchased), { align: "start", style: { marginRight: 32 } })}
                {renderCell(<span style={{ marginLeft: 12 }}></span>)}
                {renderCell("", { align: "right", style: { marginRight: 12 } })}
              </tr>
              <tr>
                {renderCell("Direct Expenses")}
                {renderCell(fmt(data.directExpenses), { align: "right" })}
                {renderCell("")}
                {renderCell("")}
              </tr>
              <tr>
                {renderCell("", { style: { border: "none", height: 4 } })}
                {renderCell("", { style: { border: "none", height: 4 } })}
                {renderCell("", { style: { border: "none", height: 4 } })}
                {renderCell("", { style: { border: "none", height: 4 } })}
              </tr>
              
              {/* Gross Profit c/o */}
              <tr>
                {renderCell(<span style={{ fontWeight: 600 }}>Gross Profit b/f<p className='text-[8px] '>This include indirect expenses</p></span>, {})}
                {renderCell(fmt(data.grossProfit), { align: "right" })}
                {renderCell(<span style={{ fontWeight: 600 }}>Gross Profit c/o</span>, {})}
                {renderCell(fmt(data.grossProfit), { align: "right" })}
              </tr>
              {/* Empty Row for separation */}
              <tr>
                {renderCell("", { style: { border: "none", height: 4 } })}
                {renderCell("", { style: { border: "none", height: 4 } })}
                {renderCell("", { style: { border: "none", height: 4 } })}
                {renderCell("", { style: { border: "none", height: 4 } })}
              </tr>
              {/* Indirect Expenses (Header/Parent row Dr side) */}
              <tr>
                {renderCell(<span style={{ fontWeight: 600 }}>Indirect Expenses</span>)}
                {renderCell(fmt(data.interestExpenses), { align: "right" })}
                {renderCell(<span style={{ fontWeight: 600 }}>Indirect Incomes</span>)}
                {renderCell(fmt(data.indirectIncome), {})}
              </tr>
              {/* Indirect Expenses detail */}
              <tr className='hidden'>
                {renderCell(<span style={{ paddingLeft: 22 }}>Indirect Expenses</span>, { italic: true })}
                {renderCell(fmt(data.indirectExpenses), { align: "right" })}
                {renderCell(<span style={{ paddingLeft: 22 }}>Dividend</span>, { italic: true })}
                {renderCell(fmt(data.indirectIncome), { align: "right" })}
              </tr>
              <tr className='hidden'>
                {renderCell(<span style={{ paddingLeft: 22 }}>Interest Expenses</span>, { italic: true })}
                {renderCell(fmt(data.interestExpenses), { align: "right" })}
                {renderCell("", {})}
                {renderCell("", {})}
              </tr>
              {/* Net Loss row */}
              <tr>
                {renderCell(<span style={{ fontWeight: 600 }}>Net P&L</span>)}
                {renderCell(fmt(data.netLoss), { align: "right" })}
                {renderCell("", {})}
                {renderCell("", {})}
              </tr>
              {/* Empty Row for separation */}
              <tr>
                {renderCell("", { style: { border: "none", height: 4 } })}
                {renderCell("", { style: { border: "none", height: 4 } })}
                {renderCell("", { style: { border: "none", height: 4 } })}
                {renderCell("", { style: { border: "none", height: 4 } })}
              </tr>
              {/* Totals */}
              <tr style={{ border: "1px solid #0000006e", borderLeft: "none", borderRight: "none" }}>
                {renderCell(<span style={{ fontWeight: 700 }}>Total</span>, { align: "center", style: { background: "#ffff" } })}
                {renderCell(fmt(data.leftTotalPL), { bold: true, align: "right", style: { background: "#ffff" } })}
                {renderCell(<span style={{ fontWeight: 700 }}>Total</span>, { align: "center", style: { background: "#ffff" } })}
                {renderCell(fmt(data.rightTotalPL), { bold: true, align: "right", style: { background: "#ffff" } })}
              </tr>
            </tbody>
          </table>
        </div>
        <div className="flex justify-end gap-3 mt-8" style={{ marginTop: 30, textAlign: "center" }}>
          <button
            className="px-4 py-2 text-sm bg-[#6742c7] text-white font-bold rounded hover:bg-[#442293]"
            style={{ margin: '14px auto 0 auto', fontSize: 16, padding: '8px 28px', background: "#6742c7", color: "#fff", borderRadius: 8, border: "none", cursor: "pointer" }}
            onClick={() => {
              setTimeout(() => {
                window.print();
              }, 50);
            }}
          >
            Print
          </button>
        </div>
      </div>
    );
  }
  function renderQTLYPerformanceModalContent(party: Party) {
    const data = buildFinalSheetDataForParty(party);

    // Periods should match what's displayed in the image
    const periods = [
      { label: "01.04.23", key: "apr_23" },
      { label: "30.06.23", key: "jun_23" },
      { label: "30.09.23", key: "sep_23" },
      { label: "31.12.23", key: "dec_23" },
      { label: "31.03.24", key: "mar_24" },
      { label: "30.06.24", key: "jun_24" },
      { label: "30.09.24", key: "sep_24" },
      { label: "31.12.24", key: "dec_24" },
      { label: "31.03.25", key: "mar_25" },
    ];

    // Helper formatter for amounts and %
    const fmt = (n: any, isPercent = false) =>
      n == null || n === ""
        ? ""
        : (
          isPercent
            ? (typeof n === "number" ? n.toFixed(2) : n) + "%"
            : (typeof n === "number"
                ? n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                : n)
        );

    // Table Definitions as in image
    const tables = [
      {
        header: "AKK NEW SHARES",
        main: "ARJUN KUMAR SITARAM START FROM 01.07.23 (Total Capital 250.00)",
        rows: [
          { label: "Overall Principal", values: periods.map(p => data?.grossProfit ?? "") },
          { label: "Overall Rate", values: periods.map(p => interestPercentage ?? ""), isPercent: true },
          { label: "Overall P&L", values: periods.map(p => data?.netLoss ?? "") }
        ]
      },
      {
        header: "SK SHARE (BG)",
        main: "SIDDHARTH KISHOR HUF START FROM 01.04.23 (Total Capital 200.00)",
        rows: [
          { label: "Gross Principal", values: periods.map(p => data?.grossProfit ?? "") },
          { label: "Gross Rate", values: periods.map(p => interestPercentage ?? ""), isPercent: true },
          { label: "Gross P&L", values: periods.map(p => data?.netLoss ?? "") }
        ]
      },
      {
        header: "ARJAY TRUST SHARES ACCOUNT",
        main: "ARJAY KISHOR TRUST START FROM 22.07.23 (Total Capital 60.00)",
        rows: [
          { label: "Gross Principal", values: periods.map(p => data?.grossProfit ?? "") },
          { label: "Gross Rate", values: periods.map(p => interestPercentage ?? ""), isPercent: true },
          { label: "Gross P&L", values: periods.map(p => data?.netLoss ?? "") }
        ]
      }
    ];

    return (
      <div style={{ background: "#fff", maxWidth: 1200, margin: "0 auto", padding: 24 }}>
        <div 
          style={{
            fontWeight: 700, 
            fontSize: 16, 
            textAlign: "center", 
            marginBottom: 10, 
            letterSpacing: 2,
            textTransform: "uppercase"
          }}
        >
          AS ON {periods[periods.length-1].label} (QTY/ PRINCIPAL/%)
        </div>

        {tables.map((block, ti) => (
          <div key={ti}
            style={{
              border: "1px solid #222",
              marginBottom: 24,
              borderRadius: 6,
              background: "#f9f9ff",
              boxShadow: "0 0 4px 1px #eee"
            }}>
            {/* Table/block header */}
            <div style={{
              fontWeight: 700,
              fontSize: 18,
              padding: "10px 12px 5px 12px",
              background: "#efeefe",
              borderBottom: "1.5px solid #d1cdf4",
              letterSpacing: 1,
              color: "#222"
            }}>
              {block.header}
            </div>
            <table style={{ borderCollapse: "collapse", width: "100%" }}>
              <thead>
                <tr>
                  <th style={{ width: 320, background: "#f2f2f2", border: "1px solid #aaa", textAlign: "left", padding: "5px 12px", fontWeight: 600, fontSize: 15 }}>
                    {block.main}
                  </th>
                  {periods.map((p, idx) => (
                    <th
                      key={p.key}
                      style={{
                        minWidth: 80,
                        background: "#e9e6fd",
                        border: "1px solid #aaa",
                        textAlign: "center",
                        padding: "4px 5px",
                        fontSize: 13,
                        fontWeight: 600
                      }}
                    >
                      {p.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, ri) => (
                  <tr key={ri}>
                    <td
                      style={{
                        padding: "6px 10px",
                        background: "#fff",
                        border: "1px solid #aaa",
                        fontWeight: 600,
                        fontSize: 14,
                        color: "#555"
                      }}
                    >
                      {row.label}
                    </td>
                    {row.values.map((value: any, vi: number) => (
                      <td
                        key={vi}
                        style={{
                          textAlign: "right",
                          padding: "6px 7px",
                          fontWeight: 500,
                          fontSize: 14,
                          background: vi % 2 === 1 ? "#f6f6fb" : "#fff",
                          border: "1px solid #ccc"
                        }}
                      >
                        {fmt(value, row.isPercent)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}

        <div className="flex justify-end gap-3 mt-8" style={{ marginTop: 20, textAlign: "center" }}>
          <button
            className="px-4 py-2 text-sm bg-[#6742c7] text-white font-bold rounded hover:bg-[#442293]"
            style={{ margin: '16px auto 0 auto', fontSize: 16, padding: '8px 28px', background: "#6742c7", color: "#fff", borderRadius: 8, border: "none", cursor: "pointer" }}
            onClick={() => {
              setTimeout(() => {
                window.print();
              }, 50);
            }}
          >
            Print
          </button>
        </div>
      </div>
    );
  }

  function renderBalanceSheetModalContent(party: Party) {
    const balance = buildBalanceSheetDetailsForParty(party);
    const headerLabel = dateTo
      ? `(As at ${new Date(dateTo).toLocaleDateString()})`
      : "select date range";
    return (
      <div style={{ background: "#fff" }}>
        <h2 style={{ fontWeight: 700, color: "#6742c7", textAlign: "center", fontSize: 22 }}>
          AKK NEW SHARES
        </h2>
        <div style={{ textAlign: "center", marginBottom: 8 }}>Jaipur</div>
        <h3 style={{
          textAlign: 'center',
          fontWeight: 700,
          fontSize: 20,
          margin: "18px 0 0 0"
        }}>BALANCE SHEET</h3>
        <div style={{ textAlign: 'center', color: "#666", marginBottom: 6 }}>
          {headerLabel}
        </div>
        <div style={{ textAlign: 'center', color: "#6742c7", marginBottom: 5, fontWeight:"bold" }}>
          Showing data for: {formatPeriod(dateFrom, dateTo)}
        </div>

        <div style={{ display: "flex", gap: 20, justifyContent: "center", alignItems: "flex-start" }}>
          <div style={{ flex: 1, minWidth: 320 }}>
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 9, color: "#6742c7" }}>LIABILITIES</div>
            <table style={{ borderCollapse: 'collapse', width: "100%", fontSize: 14 }}>
              <thead>
                <tr>
                  <th className='border-y border-[#d0d5dd] ' style={{background: '#ffff' }}>Particulars</th>
                  <th className='border-y border-[#d0d5dd] ' style={{background: '#ffff' }}>Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {balance.liabilities.map((item, idx) =>
                  item === null ? (
                    <tr key={idx}><td colSpan={2} style={{ border: "none", height: "6px" }}></td></tr>
                  ) : (
                    <tr key={idx}>
                      <td style={{fontWeight: String(item.label).includes("b>") ? 600 : 400 }}>
                        {item.label}
                      </td>
                      <td style={{
                        textAlign: 'right',
                        fontWeight: String(item.label).includes("b>") ? 600 : 400,
                        color: Number(item.amount) < 0 ? "#B91C1C" : undefined
                      }}>
                        {item.amount !== "" && item.amount !== undefined
                          ? typeof item.amount === "string" ? item.amount : Number(item.amount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : ""}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
          <div style={{ flex: 1, minWidth: 320 }}>
            <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 9, color: "#6742c7" }}>ASSETS</div>
            <table style={{ borderCollapse: 'collapse', width: "100%", fontSize: 14 }}>
              <thead>
                <tr>
                  <th className='border-y border-[#d0d5dd] ' style={{background: '#ffff' }}>Particulars</th>
                  <th className='border-y border-[#d0d5dd] ' style={{background: '#ffff' }}>Amount (₹)</th>
                </tr>
              </thead>
              <tbody>
                {balance.assets.map((item, idx) =>
                  item === null ? (
                    <tr key={idx}><td colSpan={2} style={{ border: "none", height: "6px" }}></td></tr>
                  ) : (
                    <tr key={idx}>
                      <td style={{fontWeight: String(item.label).includes("b>") ? 600 : 400 }}>
                        {item.label}
                      </td>
                      <td style={{
                
                        textAlign: 'right',
                        fontWeight: String(item.label).includes("b>") ? 600 : 400
                      }}>
                        {item.amount !== "" && item.amount !== undefined
                          ? typeof item.amount === "string" ? item.amount : Number(item.amount).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : ""}
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="flex justify-end gap-3 mt-6" style={{ marginTop: 30, textAlign: "center" }}>
          <button
            className="px-4 py-2 text-sm bg-[#6742c7] text-white font-bold rounded hover:bg-[#442293]"
            style={{ margin: '14px auto 0 auto', fontSize: 16, padding: '8px 28px', background: "#6742c7", color: "#fff", borderRadius: 8, border: "none", cursor: "pointer" }}
            onClick={() => {
              setTimeout(() => {
                window.print();
              }, 50);
            }}
          >
            Print
          </button>
        </div>
      </div>
    );
  }

  const handlePrintDownloadFinal = () => {
    if (!selectedParty) return;
    const htmlString = `
      <html>
        <head>
          <title>P&L Statement - ${selectedParty.name}</title>
          <style>
            body { font-family: Arial, sans-serif; background: #fff; color: #222; margin:40px 20px;}
            h2 { color: #6742c7;}
            th, td { border: 1px solid #d0d5dd; padding: 8px; font-size: 14px; }
            th { background: #ffff; color: #6742c7;}
            table { border-collapse: collapse; width: 100%; margin:14px 0;}
            .akktitle {font-weight:700;color: #6742c7;text-align:center;font-size:22px;}
          </style>
        </head>
        <body>
          <div class="akktitle">AKK NEW SHARES</div>
          <div style="text-align:center;margin-bottom:8px">Jaipur</div>
          <h3 style="text-align:center;font-weight:700;font-size:20px;margin:18px 0 4px 0;">PROFIT &amp; LOSS ACCOUNT</h3>
          <div style="text-align:center;color:#666;margin-bottom:5px">${
            (dateFrom || dateTo)
              ? `(For the period${dateFrom ? " from " + new Date(dateFrom).toLocaleDateString() : ""}${dateTo ? " to " + new Date(dateTo).toLocaleDateString() : ""})`
              : "(For the period from 1st April 2025 to 31st December 2025)"
          }</div>
          <div style="text-align:center;color:#6742c7;margin-bottom:5px;">Showing data for: ${formatPeriod(dateFrom, dateTo)}</div>
          <!-- Trading Account Table -->
          <div style="margin-top:25px;margin-bottom:4px;font-weight:600;font-size:17px">TRADING ACCOUNT</div>
          ${renderPnLModalContent(selectedParty).props.children[5].props.children}
          <!-- P&L Account Table -->
          <div style="margin:32px 0 6px 0;font-weight:600;font-size:16px">PROFIT &amp; LOSS ACCOUNT</div>
          ${renderPnLModalContent(selectedParty).props.children[7].props.children}
          <div style="margin-top:28px;text-align:center;">
            <button style="font-size:16px;padding:8px 28px;background:#6742c7;color:#fff;border-radius:8px;border:none;cursor:pointer;" onclick="window.print()">Print</button>
          </div>
        </body>
      </html>
    `;
    const win = window.open("", "", "width=900,height=700");
    if (!win) return;
    win.document.write(htmlString);
    win.document.close();
  };

  const handlePrintDownloadBalance = () => {
    if (!selectedParty) return;
    const balanceHtml = renderBalanceSheetModalContent(selectedParty);
    const htmlString = `
      <html>
        <head>
          <title>BALANCE SHEET - ${selectedParty.name}</title>
          <style>
            body { font-family: Arial, sans-serif; background: #fff; color: #222; margin:40px 20px;}
            h2 { color: #6742c7;}
            th, td { border: 1px solid #d0d5dd; padding: 8px; font-size: 14px; }
            th { background: #ffff; color: #6742c7;}
            table { border-collapse: collapse; width: 100%; margin:14px 0;}
            .akktitle {font-weight:700;color: #6742c7;text-align:center;font-size:22px;}
          </style>
        </head>
        <body>
          ${balanceHtml.props.children}
          <div style="margin-top:28px;text-align:center;">
            <button style="font-size:16px;padding:8px 28px;background:#6742c7;color:#fff;border-radius:8px;border:none;cursor:pointer;" onclick="window.print()">Print</button>
          </div>
        </body>
      </html>
    `;
    const win = window.open("", "", "width=1000,height=800");
    if (!win) return;
    win.document.write(htmlString);
    win.document.close();
  };

  useEffect(() => {
    if (!showPartyModal) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowPartyModal(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [showPartyModal]);

  const renderPartyModal = () => {
    if (!selectedParty) return null;
    const row = partyAggregatedRows.find(r => r.party.id === selectedParty.id);
    if (!row) return null;
    return (
      <div className="fixed top-0 left-0 right-0 bottom-0 z-40 bg-black/40 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-6 min-w-[350px] w-[96vw] max-w-5xl relative max-h-[90vh] overflow-auto print-page">
          <button
            className="absolute top-3 right-4 text-2xl text-gray-400 hover:text-gray-600"
            onClick={() => setShowPartyModal(false)}
            title="Close"
          >&times;</button>
          <h2 className="text-2xl font-bold mb-2 text-[#6742c7]">{row.party.name}</h2>
          {renderDatePeriodBanner()}
          <div className="mb-2 flex gap-2">
            <button
              className={`px-3 py-1 rounded font-semibold border text-sm ${
                partyModalSheet === "overall"
                  ? "bg-[#6742c7] text-white border-[#6742c7]"
                  : "bg-white text-[#6742c7] border-[#6742c7] hover:bg-[#F3F0FD]"
              }`}
              onClick={() => setPartyModalSheet("overall")}
            >
              Overall
            </button>
            <button
              className={`px-3 py-1 rounded font-semibold border text-sm ${
                partyModalSheet === "final"
                  ? "bg-[#6742c7] text-white border-[#6742c7]"
                  : "bg-white text-[#6742c7] border-[#6742c7] hover:bg-[#F3F0FD]"
              }`}
              onClick={() => setPartyModalSheet("final")}
            >
              Final (P&amp;L)
            </button>
            <button
              className={`px-3 py-1 rounded font-semibold border text-sm ${
                partyModalSheet === "balance"
                  ? "bg-[#6742c7] text-white border-[#6742c7]"
                  : "bg-white text-[#6742c7] border-[#6742c7] hover:bg-[#F3F0FD]"
              }`}
              onClick={() => setPartyModalSheet("balance")}
            >
              Balance Sheet
            </button>
            <button
              className={`px-3 py-1 rounded font-semibold border text-sm ${
                partyModalSheet === "QTLY Performance"
                  ? "bg-[#6742c7] text-white border-[#6742c7]"
                  : "bg-white text-[#6742c7] border-[#6742c7] hover:bg-[#F3F0FD]"
              }`}
              onClick={() => setPartyModalSheet("QTLY Performance")}
            >
              QTLY Performance
            </button>
          </div>
          {partyModalSheet === "overall" ? (
            <>
              {/* Party Summary Section - visually distinct cards for clarity */}
              <div className="mb-8 grid gap-6 md:grid-cols-3">
                {/* Contact Card */}
                <div className="bg-white rounded-2xl shadow border-l-4 border-[#7964e7] px-6 py-5 flex flex-col gap-2 hover:shadow-lg transition">
                  <div className="font-medium text-[#7964e7] uppercase text-xs mb-2 tracking-widest">Contact</div>
                  <div className="text-[16px] mb-1">
                    <span className="block text-gray-700 font-semibold">{row.party.contact_person_name || <span className="italic text-gray-400">-</span>}</span>
                    <span className="block text-gray-500">{row.party.contact_person_number || <span className="italic text-gray-400">-</span>}</span>
                  </div>
                </div>
                {/* Opening/Remaining Card */}
                <div className="bg-white rounded-2xl shadow border-l-4 border-[#41c6c4] px-6 py-5 flex flex-col gap-2 hover:shadow-lg transition">
                  <div className="font-medium text-[#41c6c4] uppercase text-xs mb-2 tracking-widest">Balance</div>
                  <div className="text-gray-600">Opening</div>
                  <div className="text-xl font-extrabold text-black mb-1">
                    {row.party.total_amount !== undefined && row.party.total_amount !== null ? row.party.total_amount : <span className="italic text-gray-400">-</span>}
                  </div>
                  <div className="text-gray-600">Remaining</div>
                  <div className="text-xl font-extrabold text-[#13a06c]">
                    {
                      typeof row.party.total_amount === "number" && typeof row.total_purchased === "number"
                        ? row.party.total_amount - row.total_purchased
                        : typeof row.party.total_amount === "string" && !isNaN(Number(row.party.total_amount)) && typeof row.total_purchased === "number"
                          ? Number(row.party.total_amount) - row.total_purchased
                          : <span className="italic text-gray-400">-</span>
                    }
                  </div>
                </div>
                {/* Totals Card */}
                <div className="bg-white rounded-2xl shadow border-l-4 border-[#ffa629] px-6 py-5 flex flex-col gap-2 hover:shadow-lg transition">
                  <div className="font-medium text-[#ffa629] uppercase text-xs mb-2 tracking-widest">Summary</div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Purchased</span>
                    <span className="font-bold text-black">{row.total_purchased}</span>
                  </div>
                  <div className="flex justify-between hidden">
                    <span className="text-gray-600">Profit&nbsp;/&nbsp;Loss</span>
                    <span className="font-bold text-black">
                      {(row.total_used - (row.total_indirect_charges + row.total_interest)) > 0
                        ? `+${row.total_used - (row.total_indirect_charges + row.total_interest)}`
                        : `${row.total_used - (row.total_indirect_charges + row.total_interest)}`}
                    
                        {/* Show the sum of total_profit_and_loss for all stock records */}
                        {row.stocks
                          .flatMap(stock => stock.records)
                          .reduce((sum, rec) => sum + (rec.total_profit_and_loss  || 0), 0)
                        }
                        {}
                    </span>
                  </div>
                  {/* --- ADD Sell sales summary --- */}
                  <div className="flex justify-between pt-2 border-t mt-2">
                    <span className="text-gray-600">Total Sold Amount</span>
                    <span className="font-bold text-black">{row.total_sales_amount !== undefined ? row.total_sales_amount : "-"}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Units Sold</span>
                    <span className="font-bold text-black">{row.total_sales_units !== undefined ? row.total_sales_units : "-"}</span>
                  </div>
                </div>
              </div>

              {/* ---- Withdrawal section for overall tab ---- */}
              <div className="mb-8">
                <h3 className="font-bold text-[#e65a23] text-lg mb-3 flex gap-2 items-center">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#FFEDE2]">
                    <svg width="20" height="20" fill="none"><circle cx="10" cy="10" r="10" fill="#FFA873"/><path d="M10 6v5m0 3v.01" stroke="#FFF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </span>
                  Withdrawals
                </h3>
                {row.withdrawals_details && row.withdrawals_details.length > 0 ? (
                  <div className="overflow-x-auto border rounded-2xl bg-[#FFFAF5]/50 p-1">
                    <table className="min-w-[420px] w-full table-auto text-[15px]">
                      <thead className="bg-[#FFEDE2]">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold text-[#e65a23]">Date</th>
                          <th className="px-3 py-2 text-left font-semibold text-[#e65a23]">Amount</th>
                          <th className="px-3 py-2 text-left font-semibold text-[#e65a23]">Categories</th>
                        </tr>
                      </thead>
                      <tbody>
                        {row.withdrawals_details
                          .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                          .map(w => (
                            <tr key={w.id} className="odd:bg-[#FFF6EF]">
                              <td className="px-3 py-2">{w.date ? new Date(w.date).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) : "-"}</td>
                              <td className="px-3 py-2 font-bold text-[#e65a23]">₹{w.amount}</td>
                              <td className="px-3 py-2">{w.catrgories || "-"}</td>
                            </tr>
                          ))
                        }
                        {/* Show total withdrawals */}
                        <tr className="bg-[#FFEDE2] font-bold">
                          <td className="px-3 py-2 text-right" colSpan={1}>Total Withdrawn:</td>
                          <td className="px-3 py-2 text-[#e65a23]">₹{row.total_withdrawal_amount}</td>
                          <td className="px-3 py-2"></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-5 text-center text-gray-400 italic bg-[#FFFAF5] rounded-xl shadow-inner border border-[#FFEDE2]">No withdrawal records found for this party.</div>
                )}
              </div>

              <h3 className="font-bold text-[#6742c7] text-xl mb-5 mt-8 flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#F3F0FD]">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 stroke-current text-[#6941C6]" fill="none" viewBox="0 0 24 24"><path d="M3 7V5a2 2 0 012-2h14a2 2 0 012 2v2m-18 0v10a2 2 0 002 2h2m-4-12h18m0 0v10a2 2 0 01-2 2h-2m-12 0h12" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"/></svg>
                </span>
                Stocks (Details per Category)
              </h3>
              {/* ... Stocks Table Section unchanged ... */}
              <div className="overflow-x-auto pb-3" style={{ maxHeight: "370px" }}>
                {row.stocks.length === 0 && (
                  <div className="p-10 text-center text-gray-400 italic bg-white rounded-lg shadow-inner border">No stocks found for this party.</div>
                )}
                {row.stocks.map((s, i) => (
                  <div
                    key={s.stock_name + '-' + i}
                    className="mb-8 last:mb-0 bg-gradient-to-tr from-[#ffff] to-[#FFF] rounded-2xl shadow overflow-hidden border border-[#f1ecff]"
                  >
                    <div className="flex justify-between items-center px-7 py-4 bg-[#6742c7] bg-opacity-80">
                      <span className="font-bold text-white tracking-wide text-lg">{s.stock_name}</span>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-[14px] mt-0 border-separate border-spacing-y-1">
                        <thead className="bg-[#ece6fa] sticky top-0 z-10">
                          <tr>
                            <th className="p-3 font-semibold text-[#6742c7] border-b border-[#eee] text-center">Category</th>
                            <th className="p-3 font-semibold text-[#6742c7] border-b border-[#eee] text-center">Unit</th>
                            <th className="p-3 font-semibold text-[#6742c7] border-b border-[#eee] text-center">Purchase Date</th>
                            <th className="p-3 font-semibold text-[#6742c7] border-b border-[#eee] text-center">Purchase Amt</th>
                            <th className="p-3 font-semibold text-[#6742c7] border-b border-[#eee] text-center">Indirect Charges</th>
                            <th className="p-3 font-semibold text-[#6742c7] border-b border-[#eee] text-center">History</th>
                          </tr>
                        </thead>
                        <tbody>
                          {s.records.map((rec, ridx) => {
                            // History Less/More expanded state using closure per record
                            const HistoryComponent = (() => {
                              const [page, setPage] = React.useState(0);
                              const MAX_ROWS = 1;
                              const shown = rec.update_history.slice(0, (page + 1) * MAX_ROWS);
                              const canShowMore = rec.update_history.length > shown.length;
                              const canShowLess = page > 0;

                              return (
                                <div>
                                  <table className="min-w-[270px] w-full border bg-white rounded shadow-inner mb-1">
                                    <thead className="bg-[#ffff]">
                                      <tr>
                                        <th className="px-2 py-1 text-xs text-[#6941C6] font-semibold border-b border-[#eee]">Date</th>
                                        <th className="px-2 py-1 text-xs text-[#6941C6] font-semibold border-b border-[#eee]">Amount</th>
                                        <th className="px-2 py-1 text-xs text-[#6941C6] font-semibold border-b border-[#eee]">P/L</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {shown.map((up, idx) => (
                                        <tr key={idx} className="odd:bg-[#f6f1ff] text-center">
                                          <td className="px-2 py-1">{up.date ? new Date(up.date).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) : "-"}</td>
                                          <td className="px-2 py-1">{up.current_amount}</td>
                                          <td className={`px-2 py-1 font-semibold ${up.profitAndLoss >= 0 ? "text-green-600" : "text-red-500"}`}>{up.profitAndLoss}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                  <div className="flex gap-2 mb-1.5">
                                    {canShowLess && (
                                      <button
                                        className="text-xs px-2 py-1 bg-[#e9e3ff] rounded-lg font-medium text-[#6742c7] outline-none hover:bg-[#d6caff] transition"
                                        onClick={() => setPage(page - 1)}
                                      >
                                        Less
                                      </button>
                                    )}
                                    {canShowMore && (
                                      <button
                                        className="text-xs px-2 py-1 bg-[#e9e3ff] rounded-lg font-medium text-[#6742c7] outline-none hover:bg-[#d6caff] transition"
                                        onClick={() => setPage(page + 1)}
                                      >
                                        More
                                      </button>
                                    )}
                                  </div>
                                </div>
                              );
                            });

                            return (
                              <tr
                                key={ridx}
                                className="bg-white hover:bg-[#f8f6ff] rounded-[12px] text-center shadow transition"
                              >
                                <td className="p-3">{rec.category}</td>
                                <td className="p-3">{rec.unit}</td>
                                <td className="p-3">{rec.date_of_purchase ? new Date(rec.date_of_purchase).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) : "-"}</td>
                                <td className="p-3">{rec.opening_balance}</td>
                                <td className="p-3">{rec.indirect_charges}</td>
                                <td className="p-3">
                                  {rec.update_history.length === 0
                                    ? <span className="text-gray-400 italic">No updates</span>
                                    : <HistoryComponent />}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex justify-end gap-3 mt-8">
              <button
            className="px-4 py-2 text-sm bg-[#6742c7] text-white font-bold rounded hover:bg-[#442293]"
            style={{ margin: '14px auto 0 auto', fontSize: 16, padding: '8px 28px', background: "#6742c7", color: "#fff", borderRadius: 8, border: "none", cursor: "pointer" }}
            onClick={() => {
              setTimeout(() => {
                window.print();
              }, 50);
            }}
          >
            Print
          </button>
              </div>
            </>
          ) : partyModalSheet === "final" ? (
            <>
              {renderPnLModalContent(row.party)}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  className="hidden px-4 py-2 text-sm bg-[#6742c7] text-white font-bold rounded hover:bg-[#442293]"
                  onClick={handlePrintDownloadFinal}
                >
                  Print/Download
                </button>
              </div>
            </>
          ) :partyModalSheet === "QTLY Performance" ? (
            <>
              {renderQTLYPerformanceModalContent(row.party)}
              
              <div className="flex justify-end gap-3 mt-6">
                <button
                  className="hidden px-4 py-2 text-sm bg-[#6742c7] text-white font-bold rounded hover:bg-[#442293]"
                  onClick={handlePrintDownloadFinal}
                >
                  Print/Download
                </button>
              </div>
            </>
          ) :(
            <>
              {renderBalanceSheetModalContent(row.party)}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  className="hidden px-4 py-2 text-sm bg-[#6742c7] text-white font-bold rounded hover:bg-[#442293]"
                  onClick={handlePrintDownloadBalance}
                >
                  Print/Download
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // ...rest of render function (balance sheet table, list table, etc, unchanged) ...

  return (
    <div className='p-6'>
      {renderGlobalFilterPanel()}
      {showPartyModal && renderPartyModal()}
      <div className='w-[100%]'>
        <div className='flex flex-wrap justify-between w-full'>
          <div className='flex items-center gap-4'>
            <h1 className='text-[28px] font-[600]'>Party Balance Sheet</h1>
            <div className='bg-[#ffff] p-2 rounded-[40px]'>
              <span className='text-[#6941C6] text-[12px]'>{parties.length} vendors</span>
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <button
              className="flex items-center gap-1 border-[1px] border-[#D0D5DD] bg-[#fff] py-2 px-4 rounded-[8px] text-[#6742c7] font-semibold"
              onClick={() => setShowFilterPanel(true)}
            >
              <FaFilter /> Filter
            </button>
            <button
              className='flex items-center gap-1 border-[1px] border-[#D0D5DD] bg-[#fff] py-2 px-4 rounded-[8px]'
              onClick={handleImportClick}
            >
              <BiImport /> Import
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
            <button
              className="flex items-center gap-1 border-[1px] border-[#1CAC70] bg-[#E6F8F0] py-2 px-4 rounded-[8px] text-[#1CAC70] font-semibold"
              onClick={handleExportCSV}
            >
              Export CSV
            </button>
          </div>
        </div>
        <span className='text-[#667085] text-[14px]'>
          Check purchase, usage (return), and earning summary per party. Use the eye icon to see all stock history and a downloadable/printable statement across all purchases and updates.
        </span>
      </div>
      {renderDatePeriodBanner()}
      <div className='flex justify-between w-[100%] mt-7'>
        <div className="w-[40%] bg-[#fff] h-[38px] flex items-center rounded-[8px] px-2 border-[#D0D5DD] border-[1px] gap-1">
          <CiSearch className="text-[#8488B7] text-[24px]" />
          <input
            type="text"
            placeholder="Search party name..."
            className="w-full bg-transparent h-[32px] focus:outline-none"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>
      </div>
      <div className="max-w-full overflow-x-auto border border-gray-200 mt-4 rounded-lg shadow-sm">
        <table className="w-full max-w-full border-collapse table-auto">
          <thead className="bg-gradient-to-r from-[#ffff] to-[#EBF9F4]">
            <tr>
              <th className="px-3 py-3 text-left text-[13px] font-semibold text-[#6742c7] border-b whitespace-nowrap relative">
                <div className="flex items-center gap-1">
                  Party Name
                  {/* ... filter header ... */}
                  <div className="inline-block relative">
                    <button
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        setOpenFilterDropdown(openFilterDropdown === 'party_name' ? null : 'party_name');
                      }}
                      className={`ml-1 text-gray-400 hover:text-[#6742c7] p-0.5 rounded-full bg-white border ${openFilterDropdown === 'party_name' ? 'ring-2 ring-[#6742c7]/30' : ''}`}
                      aria-label="Filter Party Name"
                    >
                      <FaFilter
                        size={13}
                        className={filters["party_name"] ? "text-[#6742c7]" : "text-gray-400"}
                      />
                    </button>
                    {openFilterDropdown === 'party_name' &&
                      <div className="dropdown-filter-pop absolute left-0 top-7 min-w-[160px] bg-white border border-gray-200 shadow-lg z-[9] p-3 rounded-md">
                        <div className="mb-2 text-xs text-gray-500 font-semibold pb-1 border-b">Party Name</div>
                        {uniqueColumnValues["party_name"] && uniqueColumnValues["party_name"].length > 0 ? (
                          <ul className="max-h-[220px] overflow-y-auto">
                            <li>
                              <button
                                className={`w-full text-left px-2 py-1 rounded hover:bg-[#f4f3fa] text-xs ${!filters["party_name"] ? "font-semibold text-[#6742c7]" : "text-gray-700"}`}
                                onClick={e => { e.preventDefault(); handleColumnFilterChange("party_name", null); }}
                              >
                                All
                              </button>
                            </li>
                            {uniqueColumnValues["party_name"]
                              .sort()
                              .map((option, oidx) => (
                                <li key={option + oidx}>
                                  <button
                                    className={`w-full text-left px-2 py-1 rounded hover:bg-[#f4f3fa] text-xs ${
                                      filters["party_name"] === option
                                        ? "font-semibold text-[#6742c7]" : "text-gray-700"
                                    }`}
                                    onClick={e => {
                                      e.preventDefault();
                                      handleColumnFilterChange("party_name", option);
                                    }}
                                  >
                                    {option}
                                  </button>
                                </li>
                              ))}
                          </ul>
                        ) : (
                          <span className="block px-2 text-xs text-gray-400">No values</span>
                        )}
                        <button
                          className="mt-2 px-2 py-1 text-xs text-gray-500 hover:text-[#6742c7] underline"
                          onClick={e => { e.preventDefault(); handleColumnFilterChange("party_name", null); }}
                        >
                          Clear filter
                        </button>
                      </div>
                    }
                  </div>
                </div>
              </th>
              <th className="px-3 py-3 text-left text-[13px] font-semibold text-[#6742c7] border-b whitespace-nowrap">Contact Person</th>
              <th className="px-3 py-3 text-left text-[13px] font-semibold text-[#6742c7] border-b whitespace-nowrap">Contact Number</th>
              <th className="px-3 py-3 text-left text-[13px] font-semibold text-[#6742c7] border-b whitespace-nowrap">Total Amount</th>
              <th className="px-3 py-3 text-right text-[13px] font-semibold text-[#6742c7] border-b whitespace-nowrap">Total Purchased</th>
              <th className="px-3 py-3 text-right text-[13px] font-semibold text-[#0055b7] border-b whitespace-nowrap">Remaining Amount</th>
              <th className="px-3 py-3 text-right text-[13px] font-semibold text-[#EA880D] border-b whitespace-nowrap">Total Earning</th>
              <th className="px-3 py-3 text-center text-[13px] font-semibold text-[#6742c7] border-b whitespace-nowrap">View</th>
            </tr>
          </thead>
          <tbody>
            {pageData.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center py-8 text-gray-400 font-semibold">
                  No data found.
                </td>
              </tr>
            ) : (
              pageData.map((row, idx) => (
                <>
                {console.log("row ",row)}
                <tr key={row.party.id}>
                  <td className="px-3 py-2 text-sm text-[#101828] border-b whitespace-nowrap">{row.party.name}</td>
                  <td className="px-3 py-2 text-sm text-[#101828] border-b whitespace-nowrap">{row.party.contact_person_name}</td>
                  <td className="px-3 py-2 text-sm text-[#101828] border-b whitespace-nowrap">{row.party.contact_person_number}</td>
                  <td className="px-3 py-2 text-sm text-[#101828] border-b whitespace-nowrap">{row.party.total_amount}</td>
                  <td className="px-3 py-2 text-sm text-[#344054] border-b whitespace-nowrap text-right">{row.total_purchased}</td>
                  <td className="px-3 py-2 text-sm text-[#0055b7] border-b whitespace-nowrap text-right">
                    {
                      typeof row.party.total_amount === "number" && typeof row.total_purchased === "number"
                        ? row.party.total_amount - row.total_purchased
                        : typeof row.party.total_amount === "string" && !isNaN(Number(row.party.total_amount)) && typeof row.total_purchased === "number"
                          ? Number(row.party.total_amount) - row.total_purchased
                          : "-"
                    }
                  </td>
                  <td className="px-3 py-2 text-sm text-[#EA880D] border-b whitespace-nowrap text-right">₹{row.total_earning}</td>
                  <td className="px-3 py-2 border-b whitespace-nowrap text-center">
                    <button
                      onClick={() => { setSelectedParty(row.party); setShowPartyModal(true); setPartyModalSheet("overall"); }}
                      className="flex items-center gap-1 text-[#6742C6] text-md px-2 py-1 rounded hover:bg-[#ffff] border border-transparent hover:border-[#6742C6] transition"
                      title="View/Print details"
                    >
                      <FaEye /> View
                    </button>
                  </td>
                </tr>
                </>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="flex items-center justify-between mt-4">
        <span className="text-[#344054] text-[14px]">
          Page {page} of {totalPage}
        </span>
        <div className='flex gap-2'>
          <button
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
            className="px-3 py-1 bg-[#fff] text-[#344054] border rounded-[6px]"
          >
            Previous
          </button>
          <button
            disabled={page === totalPage || filteredData.length === 0}
            onClick={() => setPage(p => p + 1)}
            className="px-3 py-1 bg-[#fff] text-[#344054] border rounded-[6px]"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default BalanceSheet;
